import React, { createContext, useContext, useState, useEffect } from 'react';

interface LoadingModalContextData {
  isLoadingModalOpen: boolean;
  setLoadingModalOpen: (isOpen: boolean) => void;
  showInstabilityModal: boolean;
  setShowInstabilityModal: (isOpen: boolean) => void;
  canShowInstabilityModal: boolean;
  blockInstabilityModal: () => void;
  shouldShowOnlyLoading: boolean;
  shouldShowOnlyInstability: boolean;
}

const LoadingModalContext = createContext<LoadingModalContextData | undefined>(
  undefined
);

export const LoadingModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [isLoadingModalOpen, setIsLoadingModalOpen] = useState(false);
  const [showInstabilityModal, setShowInstabilityModal] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);
  const [canShowInstability, setCanShowInstability] = useState(false);

  const shouldShowOnlyLoading = isLoadingModalOpen;
  const shouldShowOnlyInstability =
    !isLoadingModalOpen &&
    showInstabilityModal &&
    !isBlocked &&
    location.pathname === '/dashboard';

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (!isLoadingModalOpen && isBlocked) {
      timer = setTimeout(() => setIsBlocked(false), 500);
    }

    if (!isLoadingModalOpen && showInstabilityModal && !isBlocked) {
      timer = setTimeout(() => setCanShowInstability(true), 500);
    } else {
      setCanShowInstability(false);
    }

    return () => clearTimeout(timer);
  }, [isLoadingModalOpen, isBlocked]);

  const setLoadingModalOpen = (isOpen: boolean) => {
    setIsLoadingModalOpen(isOpen);
    if (isOpen) {
      setIsBlocked(true);
    }
  };

  return (
    <LoadingModalContext.Provider
      value={{
        isLoadingModalOpen,
        setLoadingModalOpen,
        showInstabilityModal,
        setShowInstabilityModal,
        canShowInstabilityModal: shouldShowOnlyInstability,
        blockInstabilityModal: () => setIsBlocked(true),
        shouldShowOnlyLoading,
        shouldShowOnlyInstability
      }}
    >
      {children}
    </LoadingModalContext.Provider>
  );
};

export const useLoadingModal = (): LoadingModalContextData => {
  const context = useContext(LoadingModalContext);
  if (!context) {
    throw new Error(
      'useLoadingModal must be used within a LoadingModalProvider'
    );
  }
  return context;
};
